import { pb, escapeFilterValue } from '../lib/pocketbase'
import { logger } from './logger'

const BATCH_SIZE = 10

export interface CascadeTarget {
  collection: string
  filter: string
}

export interface CascadeReport {
  deleted: Record<string, number>
  /** Collections where at least one child could not be removed, with the reason. */
  failed: { collection: string; count: number; reason: string }[]
}

/** Thrown after every target has been attempted, naming what is left behind. */
export class CascadeDeleteError extends Error {
  report: CascadeReport
  constructor(report: CascadeReport) {
    const left = report.failed.map(f => `${f.count} ${f.collection} (${f.reason})`).join(', ')
    super(`Some related records could not be removed and still point at the deleted record: ${left}`)
    this.name = 'CascadeDeleteError'
    this.report = report
  }
}

/**
 * Delete every related record in the given collections.
 *
 * Until Sep 2026 this was "best effort": a denied or failed child delete
 * was logged and forgotten, the parent was deleted anyway, and the user was
 * told everything succeeded. Under the role-based delete rules that made
 * orphaned invoices and transactions the normal outcome of an agent
 * deleting a deal. Every target is still attempted, so one bad collection
 * does not stop the others, but the result is now reported: the caller
 * gets a CascadeDeleteError naming what remains.
 */
export async function cascadeDelete(targets: CascadeTarget[]): Promise<CascadeReport> {
  const report: CascadeReport = { deleted: {}, failed: [] }

  const fetchResults = await Promise.allSettled(
    targets.map(async (target) => {
      const records = await pb.collection(target.collection).getFullList({
        filter: target.filter,
        fields: 'id',
      })
      return { collection: target.collection, records }
    })
  )

  for (let t = 0; t < fetchResults.length; t++) {
    const result = fetchResults[t]
    const collection = targets[t].collection
    if (result.status === 'rejected') {
      const reason = (result.reason as Error)?.message || String(result.reason)
      logger.warn(`Could not list ${collection} for cascade delete:`, reason)
      report.failed.push({ collection, count: -1, reason: `could not be listed: ${reason}` })
      continue
    }

    const { records } = result.value
    if (records.length === 0) continue

    let failures = 0
    let lastReason = ''
    for (let i = 0; i < records.length; i += BATCH_SIZE) {
      const batch = records.slice(i, i + BATCH_SIZE)
      const deleteResults = await Promise.allSettled(
        batch.map((r) => pb.collection(collection).delete(r.id))
      )
      for (const r of deleteResults) {
        if (r.status === 'rejected') {
          failures++
          lastReason = (r.reason as Error)?.message || String(r.reason)
        }
      }
    }
    report.deleted[collection] = records.length - failures
    if (failures > 0) {
      logger.warn(`Failed to delete ${failures}/${records.length} records from ${collection}: ${lastReason}`)
      report.failed.push({ collection, count: failures, reason: lastReason })
    } else {
      logger.info(`Deleted ${records.length} ${collection} records`)
    }
  }

  if (report.failed.length > 0) throw new CascadeDeleteError(report)
  return report
}

/**
 * Build a filter for a simple field = value match.
 */
export function fieldFilter(field: string, value: string): string {
  return `${field} = "${escapeFilterValue(value)}"`
}

/**
 * Build a filter for entity_type + entity_id pattern.
 */
export function entityFilter(typeField: string, typeValue: string, idField: string, idValue: string): string {
  return `${typeField} = "${escapeFilterValue(typeValue)}" && ${idField} = "${escapeFilterValue(idValue)}"`
}

/**
 * Build a filter for a value contained in a field: a multi-relation
 * (`client_ids ~ "id"`) or a prefixed key (`external_id ~ "birthday:x:"`).
 */
export function containsFilter(field: string, value: string): string {
  return `${field} ~ "${escapeFilterValue(value)}"`
}
